/**
 * This file contains the computation of terms from billing cycles object in planReview data
 * and other utils used for next gen subscription flows
 */

import { getContent } from '@/lib';
import { useStoreState } from '@/store';
import type {
  Maybe,
  SubscriptionBillingCycles,
  SubscriptionDetails,
} from '@/generated/graphql';

/**
 * Computes the singular or plural form for time unit
 * @param billPeriod
 * @param pluralTimeUnit
 * @returns {string}
 */
export function computeTimeUnitKey(
  billPeriod: string,
  pluralTimeUnit: boolean,
): string {
  const timeUnit = `${billPeriod.toLowerCase() ?? ''}${
    pluralTimeUnit ? 's' : ''
  }`;
  return `subscriptionCartDropdown.label.${timeUnit}`;
}

/**
 * Computes and returns content key for subscription terms
 * There can be 12 variations of subscription content
 * @param totalCycles
 * @param hasPriorTerm
 * @param pluralTimeUnit
 * @returns {string}  Content Key which must be prepended by subscriptionCartDropdown.message
 */
export function computeSubscriptionKey(
  totalCycles: string | null | undefined,
  hasPriorTerm: boolean,
  pluralTimeUnit: boolean,
): string {
  if (
    totalCycles === null ||
    totalCycles === undefined ||
    typeof totalCycles !== 'string'
  ) {
    return '';
  }

  let key = 'regularSubscriptionPeriod';
  /**
   * If a statement has hasPriorTerm=true, then the statement will begin with "Then"
   * @type {boolean}
   */
  if (hasPriorTerm) {
    // Content starts with "Then", e.g. "Then $2 for next 4 months"
    key += '.withPriorTerms';
  } else {
    // Content does NOT start with "Then"
    // Content looks like "$2 for next 4 months"
    key += '.noPriorTerms';
  }

  /**
   * Plural time unit
   * @type {boolean}
   */
  if (pluralTimeUnit) {
    // Content has: {amount} for each 2 days/weeks/months/years
    key += '.plural';
  } else {
    // Content has: {amount} for each day/week/month/year
    key += '.singular';
  }

  switch (+totalCycles) {
    case 0:
      // Content ends with: for each 2 months OR for each month
      // Content will look like $30.00 USD for each week
      key += '.recurringInfinite';
      break;
    case 1:
      // Text will look like $20.00 USD for 2 months
      // Content does NOT end with: for 3 installments
      key += '.nonRecurring';
      break;
    default:
      // Content ends with: for 3 installments
      // Content will look like $100.00 USD for each 2 months, for 3 installments
      key += '.recurring';
  }
  return key;
}

/**
 * Prepares and returns an individual term text string
 * @param billingCycle
 * @param index
 * @returns {string}
 */
export function prepareIndividualTermText(
  { fixedPrice, frequency, totalCycles }: SubscriptionBillingCycles,
  index: number,
): string {
  const billingFrequency = frequency?.intervalCount ?? 0;
  const billPeriod = frequency?.intervalUnit ?? '';
  const pluralTimeUnit = billingFrequency > 1;
  const hasPriorTerm = index > 0;
  const amount = fixedPrice?.currencyFormatSymbolISOCurrency
    ? fixedPrice?.currencyFormatSymbolISOCurrency
    : getContent('subscriptionCartDropdown.label.free');

  const computedContentKey = computeSubscriptionKey(
    totalCycles,
    hasPriorTerm,
    pluralTimeUnit,
  );
  const contentKey = `subscriptionCartDropdown.message.${computedContentKey}`;

  const templatePlaceholderData = {
    amount,
    installmentCount: totalCycles,
    timePeriod: billingFrequency,
    timeUnit: getContent(computeTimeUnitKey(billPeriod, pluralTimeUnit)),
  };

  // @ts-expect-error We know getContent will send a string
  // However i18 module only tells us reactnode
  return getContent(contentKey, templatePlaceholderData);
}

/**
 * Prepare and return a subscription terms
 * @param plan data from planReview
 * @returns {Array} Subscription Terms Text
 */
export function getTermsText(
  planResponse: SubscriptionDetails['plan'] | null | undefined,
): string[] {
  const termTextList: string[] = [];
  const billingCycles = planResponse?.billingCycles ?? [];

  billingCycles.forEach((billingCycle, index) => {
    if (billingCycle) {
      termTextList.push(prepareIndividualTermText(billingCycle, index));
    }
  });

  return termTextList;
}

/**
 * Grabs subscription details from store state
 * @returns {Object} Subscription Plan Details
 */
export function useGetSubscriptionDetails(): Maybe<SubscriptionDetails> {
  return useStoreState(({ api }) => api.subscriptionDetails);
}
